import { useState } from "react";
import { formatLoadDate } from "../loads/date";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  DocumentKind,
  StopKind,
  formatUsd,
  resolveOpenLoad,
  type Document,
} from "@haulsheet/core";
import { ProgressCard } from "../components/loads/ProgressCard";
import { STATUS_LABEL, isPaperDocument } from "../loads/lifecycle";
import { PayBreakdownTable } from "../components/check/PayBreakdownTable";
import { VerdictCard } from "../components/check/VerdictCard";
import { Button } from "../components/ui/Button";
import { getDb } from "../db/dexie";
import { contractForLoad } from "../db/contracts";
import { useAnalyzeLoad } from "../hooks/useAnalyzeLoad";
import { useAuthStore } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";
import { useUnsavedGuard } from "../pwa/unsaved";
import {
  DOCUMENT_KIND_OPTIONS,
  STORAGE_UNAVAILABLE_MESSAGE,
  isStorageUnavailable,
  uploadDocument,
  uploadErrorMessage,
} from "../api/documents";

const TABS = ["overview", "route", "docs", "history"] as const;
type Tab = (typeof TABS)[number];

export function LoadDetailScreen() {
  const { loadId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "overview";
  const me = useAuthStore((s) => s.me);
  const [error, setError] = useState<string | null>(null);
  const [uploadKind, setUploadKind] = useState<DocumentKind>(DocumentKind.pod);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [storageOff, setStorageOff] = useState(false);

  // undefined = still loading; null = no such load (e.g. deleted on another device).
  const load = useLiveQuery(
    async () => (loadId ? ((await getDb().load.get(loadId)) ?? null) : null),
    [loadId],
  );
  const stops = useLiveQuery(async () => {
    if (!loadId) return [];
    return getDb()
      .load_stop.where("loadId")
      .equals(loadId)
      .filter((s) => s.deletedAt == null)
      .sortBy("seq");
  }, [loadId]);
  const docs = useLiveQuery(async () => {
    if (!loadId) return [];
    return getDb()
      .document.where("loadId")
      .equals(loadId)
      .filter((d) => d.deletedAt == null)
      .toArray();
  }, [loadId]);
  const events = useLiveQuery(async () => {
    if (!loadId) return [];
    return getDb()
      .load_event.where("loadId")
      .equals(loadId)
      .filter((e) => e.deletedAt == null)
      .reverse()
      .sortBy("at");
  }, [loadId]);
  const broker = useLiveQuery(async () => {
    if (!load?.brokerId) return null;
    return getDb().broker.get(load.brokerId);
  }, [load?.brokerId]);
  const contact = useLiveQuery(async () => {
    if (!load?.brokerContactId) return null;
    return getDb().broker_contact.get(load.brokerContactId);
  }, [load?.brokerContactId]);
  const contract = useLiveQuery(async () => {
    const all = await getDb().pay_contract.toArray();
    return contractForLoad({ payContractId: load?.payContractId ?? null }, all);
  }, [load?.payContractId]);
  const cost = useLiveQuery(
    () => getDb().cost_profile.filter((c) => c.deletedAt == null).first(),
    [],
  );
  const truck = useLiveQuery(
    () => getDb().truck.filter((t) => t.deletedAt == null).first(),
    [],
  );

  const pay = useAnalyzeLoad(load ?? null, contract, cost, truck);
  // Server says it has no document storage (STORAGE_DISABLED): say so up front.
  const noStorage = me?.features?.documentStorage === false;
  // An upload in flight would be lost by an app-update reload.
  useUnsavedGuard("doc-upload", uploading);

  // A9: open load is the URL loadId only — never show X's contact/note/pay for Y.
  const open = loadId
    ? resolveOpenLoad({
        loadId,
        load: load ?? null,
        contact: contact ?? null,
        pay,
        payLoadId: load?.id ?? null,
      })
    : null;


  async function onUploadFile(fileList: FileList | null) {
    if (!fileList || fileList.length === 0 || !loadId || !me) return;
    const file = fileList[0]!;
    setError(null);
    setUploadMsg(null);
    setUploadError(null);
    setUploading(true);
    try {
      const result = await uploadDocument({
        file,
        kind: uploadKind,
        loadId,
      });
      const now = new Date().toISOString();
      const row: Document = {
        id: result.documentId,
        orgId: me.org.id,
        version: 1,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        loadId: result.loadId,
        expenseId: null,
        kind: result.kind,
        storageKey: result.storageKey,
        contentType: result.contentType,
        bytes: result.bytes,
        ocrText: null,
        ocrParsed: null,
      };
      await getDb().document.put(row);
      setUploadMsg(`Uploaded ${file.name}`);
      void getSyncClient().syncNow();
    } catch (err) {
      // Shown on the upload card itself. A server without document storage
      // (STORAGE_DISABLED) says so plainly; further picks are pointless.
      if (isStorageUnavailable(err)) setStorageOff(true);
      setUploadError(uploadErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  if (!loadId || load === null) {
    return <p className="muted">Load not found</p>;
  }
  if (!open || !load) {
    return <p className="muted">Loading load…</p>;
  }

  return (
    <div className="stack">
      <div className="row" style={{ alignItems: "center" }}>
        <Link to="/loads" className="muted back-link">
          ← Loads
        </Link>
        <span className="chip" data-testid="status-chip">{STATUS_LABEL[load.status] ?? load.status}</span>
      </div>
      <h1 className="screen-title">
        {load.origin.text || "Origin"} → {load.destination.text || "Dest"}
      </h1>
      {me ? (
        <ProgressCard
          load={load}
          stops={stops ?? []}
          docs={docs ?? []}
          me={me}
          onNeedPod={() => {
            setUploadKind(DocumentKind.pod);
            setParams({ tab: "docs" });
          }}
        />
      ) : null}
      <div className="row">
        {TABS.map((t) => (
          <Button
            key={t}
            variant={tab === t ? "primary" : "ghost"}
            onClick={() => setParams({ tab: t })}
          >
            {t}
          </Button>
        ))}
      </div>

      {tab === "overview" && open.pay ? (
        <>
          <div className="card stack">
            <div>
              <span className="muted">Load date</span>
              <div data-testid="load-date-value">{formatLoadDate(load.loadDate) ?? "—"}</div>
            </div>
            <div>
              <span className="muted">Broker</span>
              <div data-testid="broker-value">{broker?.name ?? "—"}</div>
            </div>
            <div>
              <span className="muted">Contact</span>
              <div>
                {open.brokerContact?.name ?? "—"}
                {open.brokerContact?.phone
                  ? ` · ${open.brokerContact.phone}`
                  : ""}
              </div>
            </div>
            <div>
              <span className="muted">Note</span>
              <div>{open.note ?? "—"}</div>
            </div>
            <div>
              <span className="muted">Ref</span>
              <div data-testid="ref-value">{load.refNumber ?? "—"}</div>
            </div>
          </div>
          <VerdictCard verdict={open.pay.verdict} />
          <PayBreakdownTable pay={open.pay} />
        </>
      ) : null}

      {tab === "route" ? (
        <div className="card stack">
          {(stops ?? []).length === 0 ? (
            <p className="muted">Stops show up here once you start the load.</p>
          ) : (
            (stops ?? []).map((s) => (
              <div key={s.id} className="list-item">
                <div>
                  <div>
                    {s.kind === StopKind.pickup ? "Pickup" : "Drop"} {s.seq} — {s.place.text}
                  </div>
                  <div className="muted">
                    {s.arrivedAt ? `In ${new Date(s.arrivedAt).toLocaleString()}` : "Not arrived"}
                    {s.departedAt
                      ? ` · Out ${new Date(s.departedAt).toLocaleString()}`
                      : ""}
                  </div>
                </div>
              </div>
            ))
          )}
          {load.routeMinutes != null ? (
            <p className="muted">
              Route {load.routeMinutes} min · DH {load.routeDeadheadMinutes ?? 0}{" "}
              min
            </p>
          ) : (
            <p className="muted">Route not computed yet</p>
          )}
        </div>
      ) : null}

      {tab === "docs" ? (
        <div className="card stack">
          {(docs ?? []).length === 0 ? (
            <p className="muted">No documents</p>
          ) : (
            (docs ?? []).map((d) => (
              <div key={d.id} className="list-item" data-testid="doc-row">
                <span className="chip">{DOCUMENT_KIND_OPTIONS.find((o) => o.value === d.kind)?.label ?? d.kind}</span>
                <span className="muted">
                  {isPaperDocument(d)
                    ? "On paper (no photo)"
                    : `${d.contentType === "application/pdf" ? "PDF" : "Photo"}${d.bytes != null ? ` · ${(d.bytes / 1024).toFixed(1)} KB` : ""}`}
                </span>
              </div>
            ))
          )}
          <div className="stack" style={{ gap: "0.5rem" }}>
            <label className="muted" htmlFor="doc-kind">
              Document kind
            </label>
            <select
              id="doc-kind"
              value={uploadKind}
              disabled={uploading}
              onChange={(e) => setUploadKind(e.target.value as DocumentKind)}
            >
              {DOCUMENT_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="muted" htmlFor="doc-file">
              File (JPEG, PNG, HEIC, or PDF · max 15 MB)
            </label>
            <input
              id="doc-file"
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.heic,.heif,.pdf"
              disabled={uploading || !me || storageOff || noStorage}
              onChange={(e) => {
                void onUploadFile(e.target.files);
                e.target.value = "";
              }}
            />
            {uploading ? <p className="muted">Uploading…</p> : null}
            {uploadMsg ? <p className="muted">{uploadMsg}</p> : null}
            {uploadError ? (
              <p className="legal-flag" role="alert" data-testid="upload-error" style={{ margin: 0 }}>
                {uploadError}
              </p>
            ) : null}
            {!me ? (
              <p className="muted">Sign in to upload</p>
            ) : noStorage ? (
              <p className="legal-flag" role="status" data-testid="docs-help" style={{ margin: 0 }}>
                {STORAGE_UNAVAILABLE_MESSAGE}, so photos can&apos;t be added. Keep the paper copies; for delivery you
                can mark the POD as on paper.
              </p>
            ) : storageOff ? null : (
              <p className="muted" data-testid="docs-help" style={{ margin: 0 }}>
                Take a photo of the rate con, BOL, or signed POD, or pick a PDF. Only your company can see them.
              </p>
            )}
          </div>
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="card" style={{ padding: 0 }}>
          {(events ?? []).length === 0 ? (
            <p className="muted" style={{ padding: "1rem" }}>
              No events
            </p>
          ) : (
            (events ?? []).map((e) => (
              <div key={e.id} className="list-item">
                <span>{eventLabel(e.kind)}</span>
                <span className="muted">
                  {new Date(e.at).toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}

      {error ? <p className="legal-flag">{error}</p> : null}
      {open.pay ? (
        <p className="muted">Keep {formatUsd(open.pay.keepCents)}</p>
      ) : null}
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  status_booked: "Booked",
  status_rolling: "On the road",
  status_delivered: "Delivered",
  status_paid: "Paid",
  status_cancelled: "Cancelled",
  status_considering: "Back to considering",
  status_passed: "Passed",
  edit_after_paid: "Edited after paid",
};

function eventLabel(kind: string): string {
  return EVENT_LABELS[kind] ?? kind.replace(/_/g, " ");
}
